import { type ReactNode, useEffect, useRef } from "react";

import {
  Button,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "@icg360/design-system";

import { hideSupportChat, showSupportChat } from "utils";

import styles from "./mss-drawer.module.scss";

export type MSSDrawerProps = {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Called when the drawer should close */
  onClose: () => void;
  /** Called when the drawer opens */
  onOpen?: () => void;
  /** Whether to show the backdrop. Defaults to true */
  showBackdrop?: boolean;
  /** Drawer content. Use MSSDrawer.Header, MSSDrawer.Body, MSSDrawer.Footer. */
  children: ReactNode;
};

export const MSSDrawer = ({
  isOpen,
  onClose,
  onOpen,
  showBackdrop = true,
  children,
}: MSSDrawerProps) => {
  const savedScrollYRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      savedScrollYRef.current = scrollY;

      hideSupportChat();

      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalLeft = document.body.style.left;
      const originalRight = document.body.style.right;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";

      return () => {
        showSupportChat();
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.left = originalLeft;
        document.body.style.right = originalRight;
        window.scrollTo(0, savedScrollYRef.current);
      };
    }
    showSupportChat();
    return () => {};
  }, [isOpen]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      showBackdrop={showBackdrop}
      className={styles.drawerPanel}
    >
      {children}
    </Drawer>
  );
};

export type MSSDrawerHeaderProps = {
  /** Drawer title */
  title: string;
  /** Optional subtitle below the title */
  subtitle?: string;
  /** Called when close is clicked. Close button is shown when this is provided. */
  onClose?: () => void;
  /** Optional data-testid for the header (wrapper). Design system close button may not be targetable. */
  closeButtonTestId?: string;
};

export const MSSDrawerHeader = ({
  title,
  subtitle,
  onClose,
  closeButtonTestId,
}: MSSDrawerHeaderProps) => (
  <div
    className={styles.drawerHeaderWrapper}
    {...(closeButtonTestId && { "data-testid": closeButtonTestId })}
  >
    <DrawerHeader
      title={title}
      subtitle={subtitle}
      onClose={onClose ?? (() => {})}
      className={`${styles.drawerHeaderCentered} !pb-md`}
    />
  </div>
);

export type MSSDrawerBodyProps = {
  /** Drawer body content */
  children: ReactNode;
  /** Optional additional class for content area */
  className?: string;
};

export const MSSDrawerBody = ({ children, className }: MSSDrawerBodyProps) => (
  <DrawerContent className={className}>{children}</DrawerContent>
);

export type MSSDrawerFooterProps = {
  /** Button text for the primary (main) action */
  primaryLabel: string;
  /** Your custom function; called when the primary button is pressed */
  onPrimary: () => void;
  /** Optional: disable the primary button (e.g. when form is invalid) */
  primaryDisabled?: boolean;
  /** Optional: show loading state on the primary button (e.g. while submitting) */
  primaryLoading?: boolean;
  /** Optional button text for the secondary action (e.g. Cancel). Omit to show only the primary button. */
  secondaryLabel?: string;
  /** Your custom function; called when the secondary button is pressed. Ignored if secondaryLabel is not provided. */
  onSecondary?: () => void;
  /** Optional: disable the secondary button */
  secondaryDisabled?: boolean;
  /** Optional: show loading state on the secondary button */
  secondaryLoading?: boolean;
  /** Optional data-testid for the primary footer button */
  primaryButtonTestId?: string;
  /** Optional data-testid for the secondary footer button */
  secondaryButtonTestId?: string;
};

export const MSSDrawerFooter = ({
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
  secondaryLoading = false,
  primaryButtonTestId,
  secondaryButtonTestId,
}: MSSDrawerFooterProps) => (
  <DrawerFooter className={styles.drawerFooterPadding}>
    <div className={styles.footerActions}>
      {secondaryLabel != null && secondaryLabel !== "" ? (
        <Button
          appearance="secondary"
          className={styles.footerBtn}
          onPress={onSecondary ?? (() => {})}
          disabled={secondaryDisabled}
          loading={secondaryLoading}
          {...(secondaryButtonTestId && {
            "data-testid": secondaryButtonTestId,
          })}
        >
          {secondaryLabel}
        </Button>
      ) : null}
      <Button
        appearance="primary"
        className={styles.footerBtn}
        onPress={onPrimary}
        disabled={primaryDisabled}
        loading={primaryLoading}
        {...(primaryButtonTestId && { "data-testid": primaryButtonTestId })}
      >
        {primaryLabel}
      </Button>
    </div>
  </DrawerFooter>
);

MSSDrawer.Header = MSSDrawerHeader;
MSSDrawer.Body = MSSDrawerBody;
MSSDrawer.Footer = MSSDrawerFooter;
